import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './Subscription.module.css';
import Card from '../components/Card';
import Badge from '../components/Badge';
import Button from '../components/Button';
import Skeleton from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import Input from '../components/Input';
import Select from '../components/Select';
import Table from '../components/Table';
import UsageMeter from '../components/UsageMeter';
import { useSubscription, useLicenseTokens, useTransactions } from '../hooks';
import { licenseTokenService } from '../services/api';
import { useToast } from '../store/ToastContext';
import { formatCurrency, formatDate, formatNumber, getDaysRemaining } from '../utils/helpers';
import {
  SubscriptionIcon,
  CalendarIcon,
  DongleIcon,
  ClockIcon,
  DownloadIcon,
  RefreshIcon,
  CopyIcon,
  TransactionIcon,
  SearchIcon,
  PlusIcon,
  XIcon,
  KeyIcon,
  SettingsIcon,
} from '../components/Icons';

function getErrorMessage(err, fallback) {
  return err.response?.data?.error || err.response?.data?.message || fallback;
}

function DocumentUsage({ documents, loading }) {
  if (loading) {
    return <Skeleton.Card lines={3} />;
  }
  if (!documents) return null;

  const isTrial = documents.is_trial;
  const unlimited = documents.unlimited;
  const licenceWord = `licence${documents.licences_total === 1 ? '' : 's'}`;
  return (
    <Card
      title="Document Usage"
      subtitle={
        unlimited
          ? `Your plan includes unlimited documents for all ${documents.licences_total} ${licenceWord}`
          : isTrial
            ? `Your free trial includes ${formatNumber(documents.document_limit)} documents`
            : `Your plan includes ${formatNumber(documents.document_limit)} documents, shared by your ${documents.licences_total} ${licenceWord}`
      }
    >
      <div className={styles.usageSection}>
        <UsageMeter
          size="lg"
          label="Documents signed"
          used={documents.documents_used}
          limit={documents.document_limit}
          unlimited={unlimited}
        />
        <div className={styles.usageStats}>
          <div className={styles.usageStat}>
            <span className={styles.usageStatValue}>{formatNumber(documents.documents_used)}</span>
            <span className={styles.usageStatLabel}>Used</span>
          </div>
          <div className={styles.usageStat}>
            <span className={styles.usageStatValue}>
              {unlimited ? 'Unlimited' : formatNumber(documents.documents_remaining)}
            </span>
            <span className={styles.usageStatLabel}>Remaining</span>
          </div>
          {!isTrial && !unlimited && (
            <div className={styles.usageStat}>
              <span className={styles.usageStatValue}>{formatNumber(documents.unallocated)}</span>
              <span className={styles.usageStatLabel}>Not given to a licence yet</span>
            </div>
          )}
        </div>
        {!isTrial && !unlimited && (
          <p className={styles.usageHint}>
            Each licence gets {formatNumber(documents.default_per_licence)} documents by default. You can give
            one licence more and another less, as long as the total stays within {formatNumber(documents.document_limit)}.
          </p>
        )}
        {documents.unassigned_used > 0 && (
          <p className={styles.usageHint}>
            Includes {formatNumber(documents.unassigned_used)} documents signed before per-licence tracking started.
          </p>
        )}
      </div>
    </Card>
  );
}

// Data comes from the page so the usage card and this list refresh together
function LicenseTokenManager({ tokens, usage, documents, loading, refetch }) {
  const toast = useToast();
  const location = useLocation();

  // Coming from the Download page ("Generate Token") -> jump straight to this section
  useEffect(() => {
    if (!loading && location.hash === '#license-tokens') {
      document.getElementById('license-tokens')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading, location.hash]);

  const [showForm, setShowForm] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [tokenLimit, setTokenLimit] = useState('');
  const [generating, setGenerating] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const canAllocate = Boolean(documents?.can_allocate);
  // Oldest first, so licence 1 is at the top (the API lists newest first).
  // A backend without document limits sends no is_current, so treat those tokens as current.
  const currentTokens = tokens.filter((t) => t.is_current !== false).sort((a, b) => a.id - b.id);
  const earlierTokens = tokens.filter((t) => t.is_current === false);
  const availableLicences = usage?.available_licences || 0;
  const canGenerate = Boolean(usage) && availableLicences > 0;

  const openForm = () => {
    setTokenName('');
    setTokenLimit(canAllocate ? String(documents.suggested_allocation ?? '') : '');
    setShowForm(true);
  };

  const handleGenerate = async () => {
    if (!tokenName.trim()) {
      toast.error('Please enter a name for the token');
      return;
    }
    let limit;
    if (canAllocate && tokenLimit !== '') {
      limit = Number(tokenLimit);
      if (!Number.isInteger(limit) || limit < 0) {
        toast.error('Document limit must be a whole number');
        return;
      }
      if (limit > documents.unallocated) {
        toast.error(`Only ${formatNumber(documents.unallocated)} documents are left to give to a licence`);
        return;
      }
    }
    setGenerating(true);
    try {
      const response = await licenseTokenService.generate(tokenName.trim(), limit);
      if (response.data.success) {
        setNewToken(response.data.data);
        setShowForm(false);
        setTokenName('');
        refetch({ silent: true });
        toast.success('License token generated');
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to generate token'));
    } finally {
      setGenerating(false);
    }
  };

  const startEditing = () => {
    setDraft(Object.fromEntries(currentTokens.map((t) => [t.id, String(t.document_limit ?? 0)])));
    setEditing(true);
  };

  const draftTotal = currentTokens.reduce((sum, t) => sum + (Number(draft[t.id]) || 0), 0);
  const draftErrors = currentTokens.reduce((errors, t) => {
    const value = draft[t.id];
    const number = Number(value);
    if (value === '' || !Number.isInteger(number) || number < 0) {
      errors[t.id] = 'Enter a whole number';
    } else if (number < (t.documents_used || 0)) {
      errors[t.id] = `Already used ${formatNumber(t.documents_used)}`;
    }
    return errors;
  }, {});
  const pool = documents?.document_limit || 0;
  const overPool = draftTotal > pool;
  const canSave = editing && !overPool && Object.keys(draftErrors).length === 0;

  const handleSaveAllocations = async () => {
    setSaving(true);
    try {
      await licenseTokenService.updateAllocations(
        currentTokens.map((t) => ({ token_id: t.id, document_limit: Number(draft[t.id]) }))
      );
      toast.success('Document limits updated');
      setEditing(false);
      refetch({ silent: true });
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update document limits'));
    } finally {
      setSaving(false);
    }
  };

  const handleCopyToken = async (token) => {
    try {
      await navigator.clipboard.writeText(token);
      toast.success('Token copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy token');
    }
  };

  const handleDownloadToken = (token) => {
    const blob = new Blob([token], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dsc-license-token.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Token file downloaded');
  };

  const getTokenBadge = (status) => {
    const variants = {
      active: { variant: 'warning', label: 'Not activated' },
      used: { variant: 'success', label: 'Activated' },
      expired: { variant: 'error', label: 'Expired' },
      revoked: { variant: 'default', label: 'Revoked' },
    };
    const v = variants[status?.toLowerCase()] || { variant: 'default', label: status };
    return <Badge variant={v.variant} size="sm">{v.label}</Badge>;
  };

  const subtitle = usage?.total_licences
    ? `${usage.created_tokens ?? currentTokens.length} of ${usage.total_licences} licence${usage.total_licences === 1 ? '' : 's'} created · each licence works on one computer`
    : 'Each licence works on one computer';

  return (
    <Card
      id="license-tokens"
      title="Licences"
      subtitle={subtitle}
      padding="none"
      headerAction={
        !loading && !editing && (
          <>
            {canAllocate && currentTokens.length > 0 && (
              <Button variant="secondary" size="sm" leftIcon={<SettingsIcon />} onClick={startEditing}>
                Adjust Limits
              </Button>
            )}
            {!showForm && (
              <Button size="sm" leftIcon={<PlusIcon />} onClick={openForm} disabled={!canGenerate}>
                Generate Token
              </Button>
            )}
          </>
        )
      }
    >
      {loading ? (
        <div className={styles.cardPadding}>
          <Skeleton.Card lines={3} />
        </div>
      ) : (
        <div className={styles.tokenSection}>
          {usage && availableLicences <= 0 && usage.total_licences > 0 && !newToken && (
            <p className={styles.tokenLimitNote}>
              All {usage.total_licences} licence{usage.total_licences === 1 ? '' : 's'} in your plan have a token.
              Upgrade your plan to add more computers.
            </p>
          )}

          {/* Generate form */}
          {showForm && (
            <div className={styles.generatePanel}>
              <div className={styles.generateFields}>
                <Input
                  label="Token name"
                  placeholder="e.g. Office PC"
                  value={tokenName}
                  onChange={(e) => setTokenName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
                  hint="Name it after the computer that will use it"
                  autoFocus
                />
                {canAllocate && (
                  <Input
                    type="number"
                    label="Document limit"
                    min={0}
                    max={documents.unallocated}
                    step={1}
                    value={tokenLimit}
                    onChange={(e) => setTokenLimit(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
                    hint={`Default ${formatNumber(documents.default_per_licence)} · ${formatNumber(documents.unallocated)} available`}
                  />
                )}
              </div>
              {canAllocate && documents.unallocated === 0 && (
                <p className={styles.warningNote}>
                  All your documents are already given to other licences. Use “Adjust Limits” to free some up,
                  or this licence will not be able to sign.
                </p>
              )}
              <div className={styles.generateActions}>
                <Button
                  variant="secondary"
                  leftIcon={<XIcon />}
                  onClick={() => { setShowForm(false); setTokenName(''); }}
                >
                  Cancel
                </Button>
                <Button onClick={handleGenerate} loading={generating} disabled={!canGenerate}>
                  Create Token
                </Button>
              </div>
            </div>
          )}

          {/* Newly generated token — highlighted */}
          {newToken && (
            <div className={styles.newTokenBox}>
              <div className={styles.newTokenHeader}>
                <KeyIcon />
                <strong>{newToken.name}</strong>
                {newToken.document_limit != null && !documents?.unlimited && (
                  <span className={styles.newTokenMeta}>{formatNumber(newToken.document_limit)} documents</span>
                )}
                <button className={styles.newTokenClose} onClick={() => setNewToken(null)} aria-label="Close">
                  <XIcon size={14} />
                </button>
              </div>
              <p className={styles.newTokenHelp}>
                Copy this token and paste it into the DSC desktop app on the computer it is for. We have also emailed it to you.
              </p>
              <div className={styles.tokenBox}>
                <code className={styles.tokenText}>{newToken.token}</code>
              </div>
              <div className={styles.tokenActions}>
                <Button variant="secondary" size="sm" leftIcon={<CopyIcon />} onClick={() => handleCopyToken(newToken.token)}>
                  Copy Token
                </Button>
                <Button variant="secondary" size="sm" leftIcon={<DownloadIcon />} onClick={() => handleDownloadToken(newToken.token)}>
                  Download
                </Button>
              </div>
            </div>
          )}

          {/* Licence list */}
          {currentTokens.length === 0 ? (
            <EmptyState
              icon={<KeyIcon size={36} />}
              title="No licence tokens yet"
              description="Generate a token for each computer where you want to use the DSC desktop app."
            />
          ) : (
            <Table>
              <Table.Head>
                <Table.Row>
                  <Table.Header>Licence</Table.Header>
                  <Table.Header>Status</Table.Header>
                  {documents && <Table.Header>{editing ? 'Document limit' : 'Documents used'}</Table.Header>}
                  <Table.Header>Created</Table.Header>
                  <Table.Header align="right">Token</Table.Header>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {currentTokens.map((t) => (
                  <Table.Row key={t.id}>
                    <Table.Cell>
                      <div className={styles.licenceName}>
                        <strong>{t.name}</strong>
                        <span className={styles.licenceMeta}>
                          {t.status === 'used'
                            ? `On ${t.used_hostname || t.used_by_username || 'a computer'}${t.used_ip ? ` · ${t.used_ip}` : ''}`
                            : 'Not activated on a computer yet'}
                        </span>
                      </div>
                    </Table.Cell>
                    <Table.Cell>{getTokenBadge(t.status)}</Table.Cell>
                    {documents && (
                      <Table.Cell className={styles.documentsCell}>
                        {editing ? (
                          <div className={styles.limitEdit}>
                            <input
                              type="number"
                              min={t.documents_used || 0}
                              step={1}
                              className={`${styles.limitInput} ${draftErrors[t.id] ? styles.limitInputError : ''}`}
                              value={draft[t.id] ?? ''}
                              onChange={(e) => setDraft({ ...draft, [t.id]: e.target.value })}
                              aria-label={`Document limit for ${t.name}`}
                            />
                            <span className={draftErrors[t.id] ? styles.limitError : styles.limitHint}>
                              {draftErrors[t.id] || `${formatNumber(t.documents_used || 0)} used`}
                            </span>
                          </div>
                        ) : (
                          <UsageMeter
                            size="sm"
                            used={t.documents_used || 0}
                            limit={t.document_limit || 0}
                            unlimited={documents.unlimited}
                          />
                        )}
                      </Table.Cell>
                    )}
                    <Table.Cell>{formatDate(t.created_at)}</Table.Cell>
                    <Table.Cell align="right">
                      <Button
                        variant="secondary"
                        size="sm"
                        leftIcon={<CopyIcon />}
                        onClick={() => handleCopyToken(t.token)}
                      >
                        Copy
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}

          {/* Allocation editor summary */}
          {editing && (
            <div className={`${styles.allocationBar} ${overPool ? styles.allocationBarError : ''}`}>
              <div className={styles.allocationText}>
                <strong>
                  {formatNumber(draftTotal)} of {formatNumber(pool)} documents given to licences
                </strong>
                <span>
                  {overPool
                    ? `That is ${formatNumber(draftTotal - pool)} more than your plan allows. Lower another licence first.`
                    : `${formatNumber(pool - draftTotal)} left to give out`}
                </span>
              </div>
              <div className={styles.allocationActions}>
                <Button variant="secondary" onClick={() => setEditing(false)} disabled={saving}>
                  Cancel
                </Button>
                <Button onClick={handleSaveAllocations} loading={saving} disabled={!canSave}>
                  Save Limits
                </Button>
              </div>
            </div>
          )}

          {/* Licences in the plan without a token yet */}
          {!editing && usage && availableLicences > 0 && currentTokens.length > 0 && (
            <p className={styles.emptySlots}>
              {availableLicences} more licence{availableLicences === 1 ? '' : 's'} available in your plan —
              generate a token for each additional computer.
            </p>
          )}

          {earlierTokens.length > 0 && (
            <details className={styles.earlierTokens}>
              <summary>Tokens from earlier subscriptions ({earlierTokens.length})</summary>
              <ul>
                {earlierTokens.map((t) => (
                  <li key={t.id}>
                    <span>{t.name}</span>
                    <span className={styles.tokenBinding}>{formatDate(t.created_at)}</span>
                    {getTokenBadge(t.status)}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </Card>
  );
}

function TransactionHistory() {
  const { transactions, loading } = useTransactions();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const filteredTransactions = transactions.filter(tx => {
    if (statusFilter && tx.status?.toLowerCase() !== statusFilter.toLowerCase()) {
      return false;
    }
    if (!search) return true;
    const searchLower = search.toLowerCase();
    const txId = tx.transaction_id || tx.id || '';
    const planName = tx.plan?.name || tx.plan || '';
    return (
      txId.toLowerCase().includes(searchLower) ||
      planName.toLowerCase().includes(searchLower)
    );
  });

  const getStatusBadge = (status) => {
    const variants = {
      completed: 'success',
      paid: 'success',
      pending: 'warning',
      failed: 'error',
    };
    return <Badge variant={variants[status?.toLowerCase()] || 'default'}>{status}</Badge>;
  };

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'completed', label: 'Completed' },
    { value: 'pending', label: 'Pending' },
    { value: 'failed', label: 'Failed' },
  ];

  return (
    <Card title="Transaction History" subtitle="View your payment history" padding="none">
      <div className={styles.txSection}>
        <div className={styles.txToolbar}>
          <Input
            placeholder="Search transactions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<SearchIcon />}
            containerClassName={styles.txSearch}
          />
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={statusOptions}
            placeholder={null}
            fullWidth={false}
            containerClassName={styles.txStatus}
          />
        </div>

        {loading ? (
          <Table.Skeleton rows={5} columns={5} />
        ) : filteredTransactions.length === 0 ? (
          <EmptyState
            icon={<TransactionIcon size={36} />}
            title="No transactions found"
            description={search || statusFilter ? 'Try adjusting your filters' : 'Your transaction history will appear here'}
          />
        ) : (
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Header>Transaction ID</Table.Header>
                <Table.Header>Date</Table.Header>
                <Table.Header>Plan</Table.Header>
                <Table.Header>Payment Method</Table.Header>
                <Table.Header align="right">Amount</Table.Header>
                <Table.Header align="center">Status</Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {filteredTransactions.map((tx) => (
                <Table.Row key={tx.id || tx.transaction_id}>
                  <Table.Cell>
                    <span className={styles.transactionId}>{tx.transaction_id || tx.id}</span>
                  </Table.Cell>
                  <Table.Cell>{formatDate(tx.created_at || tx.date)}</Table.Cell>
                  <Table.Cell>{tx.plan?.name || tx.plan || '—'}</Table.Cell>
                  <Table.Cell>{tx.payment_method || 'Razorpay'}</Table.Cell>
                  <Table.Cell align="right">{formatCurrency(tx.amount)}</Table.Cell>
                  <Table.Cell align="center">{getStatusBadge(tx.status)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </div>
    </Card>
  );
}

function Subscription() {
  const navigate = useNavigate();

  const { subscription, loading: subLoading } = useSubscription();
  const licences = useLicenseTokens();

  const loading = subLoading;

  const getStatusBadge = (status) => {
    const variants = {
      active: 'success',
      expired: 'error',
      pending: 'warning',
    };
    return <Badge variant={variants[status?.toLowerCase()] || 'default'} size="lg">{status}</Badge>;
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Skeleton variant="rectangular" height={24} width={180} />
        </div>
        <Skeleton.Card lines={4} />
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <h1 className={styles.title}>Subscription</h1>
        </header>
        <Card>
          <EmptyState
            icon={<SubscriptionIcon size={48} />}
            title="No active subscription"
            description="Get started with a plan to access all DSC features"
            action={() => navigate('/plans')}
            actionLabel="View Plans"
          />
        </Card>
        <TransactionHistory />
      </div>
    );
  }

  // Map API response fields
  const isTrial = Boolean(subscription.is_trial);
  const planName = isTrial ? 'Free Trial' : subscription.plan?.name || subscription.plan_name || 'Standard';
  const dongleCount = parseInt(subscription.number_of_dongles || subscription.dongle_count, 10) || 0;
  const startDate = subscription.start_date;
  const endDate = subscription.end_date;
  const status = subscription.status;
  const totalDays = parseInt(subscription.plan?.duration_days || subscription.total_days, 10) || 365;
  const daysRemaining = getDaysRemaining(endDate) || 0;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Subscription</h1>
          <p className={styles.subtitle}>Your plan, licences and document usage</p>
        </div>
        <div className={styles.headerActions}>
          <Button variant="secondary" onClick={() => navigate('/plans')}>
            {isTrial ? 'Choose a Plan' : 'Upgrade Plan'}
          </Button>
          {!isTrial && daysRemaining <= 30 && (
            <Button leftIcon={<RefreshIcon />} onClick={() => navigate('/plans')}>
              Renew
            </Button>
          )}
        </div>
      </header>

      {/* Subscription Details */}
      <Card>
        <div className={styles.subscriptionCard}>
          <div className={styles.planSection}>
            <div className={styles.planHeader}>
              <h2 className={styles.planName}>{isTrial ? planName : `${planName} Plan`}</h2>
              {getStatusBadge(status)}
            </div>
            <div className={styles.detailsGrid}>
              <div className={styles.detailItem}>
                <CalendarIcon />
                <div className={styles.detailContent}>
                  <span className={styles.detailLabel}>Start Date</span>
                  <span className={styles.detailValue}>{formatDate(startDate)}</span>
                </div>
              </div>
              <div className={styles.detailItem}>
                <CalendarIcon />
                <div className={styles.detailContent}>
                  <span className={styles.detailLabel}>End Date</span>
                  <span className={styles.detailValue}>{formatDate(endDate)}</span>
                </div>
              </div>
              <div className={styles.detailItem}>
                <DongleIcon />
                <div className={styles.detailContent}>
                  <span className={styles.detailLabel}>Licences</span>
                  <span className={styles.detailValue}>{dongleCount}</span>
                </div>
              </div>
              <div className={styles.detailItem}>
                <ClockIcon />
                <div className={styles.detailContent}>
                  <span className={styles.detailLabel}>Days Remaining</span>
                  <span className={styles.detailValue}>{daysRemaining}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className={styles.progressSection}>
            <div className={styles.progressHeader}>
              <span className={styles.progressLabel}>Subscription Progress</span>
              <span className={styles.progressValue}>{daysRemaining} days left</span>
            </div>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{ width: `${Math.max(0, (daysRemaining / totalDays) * 100)}%` }}
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Document Usage */}
      <DocumentUsage documents={licences.documents} loading={licences.loading} />

      {/* License Tokens */}
      <LicenseTokenManager {...licences} />

      {/* Transaction History */}
      <TransactionHistory />
    </div>
  );
}

export default Subscription;
